/**
 * Paint a prefix of a streaming assistant body without breaking markdown.
 *
 * The server replaces the whole comment body in bursts. This stepper walks toward
 * that latest string. A cut never lands inside a grapheme, a sco embed, a fence,
 * inline code, a link, or emphasis. Those regions are jumped once they are closed
 * in the target, and held at their start while they are still open. Anything that
 * is not an extension of the text already on screen snaps to the target.
 */

export const REVEAL_BASE_CPS = 160
export const REVEAL_MAX_LAG_SEC = 0.45
/** Longer than the server publish interval, so a live burst does not count as finished. */
export const REVEAL_SETTLE_MS = 700

const FINISHED_THINKING = new Set(['complete', 'error'])

export function shouldRevealAssistantText(options: {
  isAiMessage: boolean
  unsent: boolean
  mountedEmpty: boolean
  prefersReducedMotion: boolean
  thinkingStatus?: string | null
}): boolean {
  if (!options.isAiMessage || options.unsent || !options.mountedEmpty || options.prefersReducedMotion) {
    return false
  }
  if (options.thinkingStatus && FINISHED_THINKING.has(options.thinkingStatus)) {
    return false
  }
  return true
}

export function graphemeBudget(behind: number, elapsedMs: number): number {
  if (behind <= 0 || elapsedMs <= 0) {
    return 0
  }
  const cps = Math.max(REVEAL_BASE_CPS, behind / REVEAL_MAX_LAG_SEC)
  return Math.max(1, Math.round((cps * elapsedMs) / 1000))
}

export function stepRevealedText(
  shown: string,
  target: string,
  maxGraphemes: number,
  flush: boolean
): string {
  if (flush || !target.startsWith(shown)) {
    return target
  }
  if (maxGraphemes <= 0 || shown.length >= target.length) {
    return shown
  }

  const proposed = indexAfterGraphemes(target, shown.length, maxGraphemes)
  const end = clampSafeEnd(target, shown.length, proposed)
  if (end <= shown.length) {
    return shown
  }
  return target.slice(0, end)
}

type Region = { start: number; end: number; closed: boolean }

function clampSafeEnd(text: string, from: number, proposed: number): number {
  let end = proposed
  for (const region of findAtomicRegions(text)) {
    if (region.end <= from) {
      continue
    }
    const fromInside = from > region.start && from < region.end
    const endInside = end > region.start && end < region.end
    if (!region.closed) {
      if (fromInside) {
        return from
      }
      if (end > region.start) {
        end = region.start
      }
      break
    }
    if (fromInside || endInside) {
      end = region.end
    }
  }
  return end < from ? from : end
}

function findAtomicRegions(text: string): Region[] {
  const regions: Region[] = []
  let i = 0
  while (i < text.length) {
    const region =
      matchFence(text, i) ||
      matchSco(text, i) ||
      matchInlineCode(text, i) ||
      matchLink(text, i) ||
      matchEmphasis(text, i)
    if (!region) {
      i += 1
      continue
    }
    regions.push(region)
    if (!region.closed || region.end <= i) {
      break
    }
    i = region.end
  }
  return regions
}

function matchFence(text: string, i: number): Region | null {
  if (!(i === 0 || text[i - 1] === '\n')) {
    return null
  }
  let j = i
  while (text[j] === ' ' && j - i < 4) {
    j += 1
  }
  if (j - i > 3) {
    return null
  }
  const marker = text[j]
  if (marker !== '`' && marker !== '~') {
    return null
  }
  let len = 0
  while (text[j + len] === marker) {
    len += 1
  }
  if (len < 3) {
    return null
  }
  const lineEnd = text.indexOf('\n', j + len)
  if (lineEnd === -1) {
    return { start: i, end: text.length, closed: false }
  }
  let p = lineEnd + 1
  while (p < text.length) {
    let s = p
    while (text[s] === ' ' && s - p < 4) {
      s += 1
    }
    if (s - p <= 3 && text[s] === marker) {
      let count = 0
      while (text[s + count] === marker) {
        count += 1
      }
      if (count >= len) {
        let t = s + count
        while (t < text.length && (text[t] === ' ' || text[t] === '\t')) {
          t += 1
        }
        if (t >= text.length || text[t] === '\n') {
          const end = text[t] === '\n' ? t + 1 : text.length
          return { start: i, end, closed: true }
        }
      }
    }
    const next = text.indexOf('\n', p)
    if (next === -1) {
      break
    }
    p = next + 1
  }
  return { start: i, end: text.length, closed: false }
}

function matchSco(text: string, i: number): Region | null {
  if (text[i] !== '<') {
    return null
  }
  const head = text.slice(i, i + 20).toLowerCase()
  let name: 'question' | 'citation' | null = null
  if (head.startsWith('<sco-question')) {
    name = 'question'
  } else if (head.startsWith('<sco-citation')) {
    name = 'citation'
  }
  if (!name) {
    return null
  }
  const boundary = text[i + '<sco-'.length + name.length]
  if (boundary && boundary !== '>' && boundary !== '/' && !/\s/.test(boundary)) {
    return null
  }
  const gt = text.indexOf('>', i)
  if (gt === -1) {
    return { start: i, end: text.length, closed: false }
  }
  const openTag = text.slice(i, gt + 1)
  if (/\/\s*>$/.test(openTag)) {
    return { start: i, end: gt + 1, closed: true }
  }
  const close = `</sco-${name}`
  const lower = text.toLowerCase()
  let from = gt + 1
  while (from < text.length) {
    const found = lower.indexOf(close, from)
    if (found === -1) {
      return { start: i, end: text.length, closed: false }
    }
    let k = found + close.length
    while (k < text.length && /\s/.test(text[k])) {
      k += 1
    }
    if (text[k] === '>') {
      return { start: i, end: k + 1, closed: true }
    }
    from = found + 1
  }
  return { start: i, end: text.length, closed: false }
}

function matchInlineCode(text: string, i: number): Region | null {
  if (text[i] !== '`') {
    return null
  }
  let len = 0
  while (text[i + len] === '`') {
    len += 1
  }
  const closer = findRun(text, i + len, '`', len)
  if (closer === -1) {
    return { start: i, end: text.length, closed: false }
  }
  return { start: i, end: closer + len, closed: true }
}

function matchLink(text: string, i: number): Region | null {
  let start = i
  if (text[i] === '!' && text[i + 1] === '[') {
    start = i
  } else if (text[i] !== '[') {
    return null
  }
  const contentStart = text[start] === '!' ? start + 2 : start + 1
  const closeBracket = text.indexOf(']', contentStart)
  if (closeBracket === -1) {
    if (text.indexOf('\n', contentStart) === -1) {
      return { start, end: text.length, closed: false }
    }
    return null
  }
  if (text[closeBracket + 1] !== '(') {
    return null
  }
  const closeParen = text.indexOf(')', closeBracket + 2)
  if (closeParen === -1) {
    return { start, end: text.length, closed: false }
  }
  return { start, end: closeParen + 1, closed: true }
}

function matchEmphasis(text: string, i: number): Region | null {
  const char = text[i]
  if (char !== '*' && char !== '_' && char !== '~') {
    return null
  }
  const run = runLength(text, i, char)
  if ((char === '*' || char === '_') && isThematicBreak(text, i)) {
    const lineEnd = text.indexOf('\n', i)
    return { start: i, end: lineEnd === -1 ? text.length : lineEnd + 1, closed: true }
  }
  // Pair ** / *** / ~~ with a closer of the same length. A longer run is left as
  // literal characters so a rule or a typo cannot hide the rest of the reply.
  if ((run === 2 || run === 3) && (char === '*' || char === '_')) {
    return matchDelim(text, i, char.repeat(run))
  }
  if (char === '~' && run === 2) {
    return matchDelim(text, i, '~~')
  }
  if (char === '*' && isListMarker(text, i)) {
    return null
  }
  if (char === '*' || char === '_') {
    return matchSingle(text, i, char)
  }
  return null
}

function isThematicBreak(text: string, i: number): boolean {
  const lineStart = text.lastIndexOf('\n', i - 1) + 1
  const lineEndIdx = text.indexOf('\n', i)
  const line = text.slice(lineStart, lineEndIdx === -1 ? text.length : lineEndIdx)
  return /^\s{0,3}([*_])(\s*\1){2,}\s*$/.test(line)
}

function matchDelim(text: string, i: number, delim: string): Region | null {
  if (!text.startsWith(delim, i)) {
    return null
  }
  const closer = findRun(text, i + delim.length, delim[0], delim.length)
  if (closer === -1) {
    return { start: i, end: text.length, closed: false }
  }
  return { start: i, end: closer + delim.length, closed: true }
}

function matchSingle(text: string, i: number, char: string): Region | null {
  if (text[i] !== char || text[i + 1] === char) {
    return null
  }
  const prev = i === 0 ? '' : text[i - 1]
  if (prev && isWordChar(prev)) {
    return null
  }
  const next = text[i + 1]
  if (!next || /\s/.test(next)) {
    return null
  }
  for (let j = i + 1; j < text.length; j += 1) {
    if (text[j] !== char || text[j + 1] === char) {
      continue
    }
    const before = text[j - 1]
    const after = text[j + 1] || ''
    if (/\s/.test(before)) {
      continue
    }
    if (after && isWordChar(after)) {
      continue
    }
    return { start: i, end: j + 1, closed: true }
  }
  return { start: i, end: text.length, closed: false }
}

function isListMarker(text: string, i: number): boolean {
  const lineStart = text.lastIndexOf('\n', i - 1) + 1
  let j = lineStart
  while (text[j] === ' ' && j - lineStart < 4) {
    j += 1
  }
  if (j !== i) {
    return false
  }
  return (
    (text[i] === '*' || text[i] === '-' || text[i] === '+') && (text[i + 1] === ' ' || text[i + 1] === '\t')
  )
}

function isWordChar(char: string): boolean {
  return /[\p{L}\p{N}]/u.test(char)
}

function runLength(text: string, i: number, char: string): number {
  let n = 0
  while (text[i + n] === char) {
    n += 1
  }
  return n
}

function findRun(text: string, from: number, char: string, len: number): number {
  for (let j = from; j <= text.length - len; j += 1) {
    if (text[j - 1] === char) {
      continue
    }
    let count = 0
    while (text[j + count] === char) {
      count += 1
    }
    if (count === len) {
      return j
    }
    if (count > 1) {
      j += count - 1
    }
  }
  return -1
}

let segmenter: Intl.Segmenter | null = null

function getSegmenter(): Intl.Segmenter {
  if (!segmenter) {
    segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' })
  }
  return segmenter
}

export function graphemeCount(text: string): number {
  return Array.from(getSegmenter().segment(text)).length
}

function indexAfterGraphemes(text: string, from: number, count: number): number {
  if (count <= 0) {
    return from
  }
  let left = count
  let index = from
  for (const part of getSegmenter().segment(text.slice(from))) {
    index += part.segment.length
    left -= 1
    if (left <= 0) {
      break
    }
  }
  return index
}
